import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Authentication configuration types
export interface WebhookAuth {
  type: 'none' | 'bearer' | 'basic' | 'api_key' | 'url_token' | 'body_key' | 'custom' | 'zoho_oauth';
  token?: string;
  username?: string;
  password?: string;
  api_key?: string;
  header_name?: string;
  header_value?: string;
  param_name?: string;
  key_name?: string;
  key_value?: string;
  // Zoho OAuth specific fields
  client_id?: string;
  client_secret?: string;
  refresh_token?: string;
  token_url?: string;
}

// Webhook configuration
export interface WebhookConfig {
  auth?: WebhookAuth;
  payload_format?: 'json' | 'leadsquared' | 'zoho_crm' | 'zoho_function' | 'custom' | 'form_data';
  grade_mapping?: Record<string, string>;
  static_fields?: Record<string, string>;
  default_values?: Record<string, string>; // Default values when fields are empty/N/A
  headers?: Record<string, string>;
  custom_template?: string | object;
  phone_prefix?: string; // For zoho_function: adds prefix to phone fields (e.g., "+91")
  timeout?: number;
}

export interface Webhook {
  id: number;
  client_id: number;
  webhook_name: string;
  webhook_url: string;
  is_active: boolean;
  field_mapping?: Record<string, string>;
  config?: WebhookConfig;
  created_at: string;
  updated_at: string;
}

export interface WebhookLog {
  id: number;
  webhook_id: number;
  lead_id?: number;
  status: 'sent' | 'failed';
  status_code?: number;
  request_payload?: any;
  response_data?: any;
  error_message?: string;
  sent_at: string;
  created_at: string;
}

export interface WebhookStats {
  total: number;
  success: number;
  duplicate: number;
  apiError: number;
  networkError: number;
  successRate: number;
}

export interface LeadField {
  field: string;
  label: string;
  type: string;
  category?: string;
}

export interface AuthType {
  value: string;
  label: string;
  description: string;
}

export interface PayloadFormat {
  value: string;
  label: string;
  description: string;
  example: string;
}

class WebhookService {
  private getAuthHeaders() {
    const token = Cookies.get('crm_auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getWebhooks(): Promise<Webhook[]> {
    const response = await axios.get(`${API_BASE_URL}/webhooks`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async getWebhookById(id: number): Promise<Webhook> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/${id}`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async createWebhook(webhook: Partial<Webhook>): Promise<Webhook> {
    const response = await axios.post(`${API_BASE_URL}/webhooks`, webhook, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async updateWebhook(id: number, webhook: Partial<Webhook>): Promise<Webhook> {
    const response = await axios.put(`${API_BASE_URL}/webhooks/${id}`, webhook, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async deleteWebhook(id: number): Promise<void> {
    await axios.delete(`${API_BASE_URL}/webhooks/${id}`, {
      headers: this.getAuthHeaders()
    });
  }

  async testWebhook(webhookUrl: string, config?: WebhookConfig): Promise<any> {
    const response = await axios.post(`${API_BASE_URL}/webhooks/test`, {
      webhook_url: webhookUrl,
      config
    }, {
      headers: this.getAuthHeaders()
    });
    return response.data;
  }

  async getWebhookLogs(
    id: number,
    limit = 50,
    offset = 0,
    startDate?: string,
    endDate?: string,
    search?: string,
    statusFilter?: string
  ): Promise<{ logs: WebhookLog[], total: number, hasMore: boolean }> {
    const params: Record<string, any> = { limit, offset };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (search) params.search = search;
    if (statusFilter) params.status_filter = statusFilter;

    const response = await axios.get(`${API_BASE_URL}/webhooks/${id}/logs`, {
      params,
      headers: this.getAuthHeaders()
    });
    return {
      logs: response.data.data,
      total: response.data.pagination.total,
      hasMore: response.data.pagination.hasMore ?? (offset + response.data.data.length < response.data.pagination.total)
    };
  }

  async getWebhookStats(id: number, startDate?: string, endDate?: string): Promise<WebhookStats> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/${id}/stats`, {
      params: { start_date: startDate, end_date: endDate },
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async getAvailableFields(): Promise<LeadField[]> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/fields`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async getAuthTypes(): Promise<AuthType[]> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/auth-types`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async getPayloadFormats(): Promise<PayloadFormat[]> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/payload-formats`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async getGradeMappings(): Promise<Record<string, string>> {
    const response = await axios.get(`${API_BASE_URL}/webhooks/grade-mappings`, {
      headers: this.getAuthHeaders()
    });
    return response.data.data;
  }

  async resendWebhook(webhookId: number, logId: number): Promise<{ success: boolean; message: string; data?: any }> {
    const response = await axios.post(
      `${API_BASE_URL}/webhooks/${webhookId}/resend/${logId}`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export default new WebhookService();
